<template>
  <loader-content :ref="(el: any) => loader = el">
    <div class="league-descriptions">
      <a href="#" @click.prevent="router.push({name: 'profile'})" class="nav-back">
        <svg-icon icon="back"/> <span class="text-capitalize-first">{{$t('profile')}}</span>
      </a>
      <h1>{{$t('league_descriptions')}}</h1>

      <div
        v-for="league in state.leagues"
        :key="league.league"
        :id="`league-${league.league}`"
        class="league-card"
      >
        <div class="d-flex flex-row align-items-center">
          <div class="icon">
            <img alt="league" :src="`/img/leagues/${league.league}.png`">
          </div>
          <div class="info">
            <span class="league-name">{{ $t(`leagues.titles.${league.league}`) }}</span>
            <span class="requirement">
              {{ league.minBetSum.amount === '0'
                ? $t('league_requirement_start')
                : $t('league_requirement', {betSum: numberFormatter.formatNumber(league.minBetSum), deposits: league.minDepositsCount}) }}
            </span>
          </div>
        </div>

        <div class="rules">
          <div class="info">
            <div class="item">
              <div>{{$t('league_wheel_of_fortune')}}</div>
              <div>{{$t('league_wheel_of_fortune_range', {min: numberFormatter.formatNumber(league.wheelMinReward), max: numberFormatter.formatNumber(league.wheelMaxReward)})}}</div>
            </div>
            <div class="item" v-if="league.tournaments.length > 0">
              <div>{{$t('league_tournament_start_bankroll')}}</div>
              <div>{{numberFormatter.formatNumber(league.tournaments[0].startBankroll)}}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </loader-content>
</template>

<style scoped>
.nav-back {
  display: flex;
  gap: var(--margin-xs);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.5%;
  align-items: center;
  color: #FFFFFFA3;
  text-decoration: none;
  padding-bottom: var(--margin-sm);
}

.league-descriptions {
  display: flex;
  flex-direction: column;
  margin: 0 var(--margin-normal);
  gap: var(--margin-normal);
  scroll-margin-top: var(--margin-normal);
}

.league-card {
  display: flex;
  flex-direction: column;
  gap: var(--margin-sm);
  background-color: var(--header-bg);
  border-radius: var(--border-radius);
  padding: var(--margin-normal);
  scroll-margin-top: var(--margin-normal);

  .icon {
    margin-right: var(--margin-normal);
    img {
      width: 60px;
      height: 60px;
    }
  }

  .info {
    display: flex;
    flex-direction: column;

    .league-name {
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0%;
    }

    .requirement {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: 0%;
      color: #FFFFFF99;
    }
  }

  .rules {
    display: flex;
    flex-direction: column;

    h2 {
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0%;
    }

    .description {
      padding-top: var(--margin-xs);
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0%;
      color: #FFFFFFA3;
    }

    .info {
      display: flex;
      flex-direction: column;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0%;
      color: #FFFFFFA3;

      .item {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px dashed #FFFFFF12;
        padding-top: var(--margin-xs);
        padding-bottom: var(--margin-xs);

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}
</style>

<script lang="ts" setup>
import {reactive} from "@vue/reactivity";
import {api} from "@/service/APIClient";
import {nextTick, onMounted, ref} from "vue";
import {useStore} from "vuex";
import {useRouter} from "vue-router";
import {$t} from "@/i18n";
import SvgIcon from "@/components/SvgIcon.vue";
import LoaderContent from "@/components/LoaderContent.vue";
import LeagueDTO from "@/models/LeagueDTO";
import UserDTO from "@/models/UserDTO";
import {numberFormatter} from "@/service/NumberFormatter";

const state = reactive({
  leagues: [] as LeagueDTO[],
});

const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
const store = useStore()
const router = useRouter()

onMounted(async () => {
  await loader.value?.load(async () => {
    state.leagues = await api.call.leagues.list()
  });

  await nextTick()
  const user = store.getters.getUser as UserDTO|null
  if (!user) {
    return
  }

  document.getElementById(`league-${user.userLeague.league}`)?.scrollIntoView({behavior: 'smooth', block: 'start'})
})
</script>
